import { computed, inject, Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { switchMap } from 'rxjs/operators'

import * as fromAccount from '@account/ngrx'
import type { CreditsSpent } from '@app/models/api'
import { NegativeBalanceComponent } from '@app/modules/credits/components/negative-balance/negative-balance.component'
import { PurchaseStatusService } from '@app/modules/credits/services/purchase-status.service'
import { PopupService } from '@app/modules/popup/popup.service'
import { ModalService } from '@app/overlays/modal/modal.service'
import { MemberService } from '@app/services/member.service'
import { StorageService } from '@app/services/storage.service'
import type * as fromRoot from '@store/root'

@Injectable({
  providedIn: 'root',
})
export class CreditsStore {
  private readonly storageService = inject(StorageService)
  private readonly memberService = inject(MemberService)
  private readonly modalService = inject(ModalService)
  private readonly popupService = inject(PopupService)
  private readonly purchaseStatusService = inject(PurchaseStatusService)
  private readonly store = inject<Store<fromRoot.State>>(Store)

  private readonly balance = this.store.selectSignal(fromAccount.Selectors.creditBalance)
  private readonly isBalanceNegative = computed(() => (this.balance() || 0) < 0)
  private negativeBalanceBlock = false

  private readonly key = 'negativeBalanceBlock'

  constructor() {
    this.storageService.getWithFallback(this.key, false).then(value => {
      if (value) {
        this.negativeBalanceBlock = true
      }
    })
  }

  init(): void {
    this.purchaseStatusService.purchaseComplete$
      .pipe(switchMap(() => this.memberService.reloadCurrentData()))
      .subscribe(() => {
        this.negativeBalanceBlock = this.isBalanceNegative()
        void this.storageService.safeSet(this.key, this.negativeBalanceBlock)
        this.handleNegativeBalance()
      })
  }

  private showNegativeBalanceModal(): void {
    // HACK for closing the modal by a frame close after navigation. Should be fixed by XB-11239
    setTimeout(() => {
      this.popupService.askForShowing('negative-balance', () => {
        const { componentRef } = this.modalService.create(NegativeBalanceComponent, { padded: false })
        componentRef.setInput('balance', this.memberService.account.token_balance)
        return { componentRef }
      })
    }, 0)
  }

  handleNegativeBalance(): boolean {
    if (this.isBalanceNegative() && this.negativeBalanceBlock) {
      this.showNegativeBalanceModal()
      return true
    }

    return false
  }

  onCreditsSpent(response: CreditsSpent): void {
    if (
      response.token_balance !== undefined &&
      (this.memberService.account.has_fake_token_balance ||
        response.token_balance !== this.memberService.account.token_balance)
    ) {
      this.memberService.updateCreditsBalance(response.token_balance)
    }
  }

  onFakeCreditsSpent(amount: number): void {
    this.store.dispatch(fromAccount.Actions.fakeCreditsSpent({ amount }))
  }
}
